import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetOnlineOrders,
  CreateOnlineOrder,
  UpdateOnlineOrder,
  DeleteOnlineOrder } from "../Api";
import { ServiceQueryOptions } from "./query_helpers";
import { DEFAULT_STALE_TIME } from "../Constant/AppConstants";

export const onlineOrderQueryKeys = {
  onlineOrders: (params?: Parameters<typeof GetOnlineOrders>[0]) =>
    ["onlineOrders", params] as const,
};

export const invalidateOnlineOrderQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["onlineOrders"] });
};

export const useOnlineOrdersQuery = (
  params?: Parameters<typeof GetOnlineOrders>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: onlineOrderQueryKeys.onlineOrders(params),
    queryFn: async () => {
      const res = await GetOnlineOrders(params);
      if (!res?.success) {
        return { data: [], total: 0 };
      }
      return {
        data: res.data || [],
        total: res.meta?.total || 0,
      };
    },
  });
};

export const useCreateOnlineOrderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateOnlineOrder>[0]) =>
      CreateOnlineOrder(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateOnlineOrderQueries(queryClient);
      }
    },
  });
};

export const useUpdateOnlineOrderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateOnlineOrder>[0]) =>
      UpdateOnlineOrder(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateOnlineOrderQueries(queryClient);
      }
    },
  });
};

export const useDeleteOnlineOrderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof DeleteOnlineOrder>[0]) =>
      DeleteOnlineOrder(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateOnlineOrderQueries(queryClient);
      }
    },
  });
};

export const useOnlineOrderService = (
  params?: Parameters<typeof GetOnlineOrders>[0],
) => {
  const onlineOrdersQuery = useOnlineOrdersQuery(params);
  const createOnlineOrderMutation = useCreateOnlineOrderMutation();
  const updateOnlineOrderMutation = useUpdateOnlineOrderMutation();
  const deleteOnlineOrderMutation = useDeleteOnlineOrderMutation();

  return {
    onlineOrdersQuery,
    createOnlineOrderMutation,
    updateOnlineOrderMutation,
    deleteOnlineOrderMutation,
  };
};
